import React, { useState, useMemo } from 'react';
import { View, FlatList, StyleSheet, TouchableOpacity, ActivityIndicator, ScrollView } from 'react-native';
import { Text, Searchbar, Chip } from 'react-native-paper';
import Icon from 'react-native-vector-icons/FontAwesome';
import { useSelector } from 'react-redux';
import type { RootState } from '../redux/store';
import { useGetAmAssetsQuery } from '../redux/services/amassetApi';
import AmPhoto from '../Components/AmPhoto';
import { assetAge } from '../Common/assetAge';

import T from '../Components/T';
import { tp } from '../utils/translateText';
/** Asset Register: every asset with its photo, department, location, age and status. */
const AmAssetRegister = ({ navigation }: any) => {
  const company = useSelector((state: RootState) => state.gdata?.company);
  const [search, setSearch] = useState('');
  const [dept, setDept] = useState<string | null>(null);

  const { data: assets = [], isLoading } = useGetAmAssetsQuery(company || '', { skip: !company });

  const departments = useMemo(
    () => Array.from(new Set(assets.map(a => a.dp_name).filter(Boolean))) as string[],
    [assets]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return assets.filter(a => {
      if (dept && a.dp_name !== dept) return false;
      if (!q) return true;
      return [a.as_code, a.as_name, a.as_serialno, a.as_location, a.loc_name, a.dp_name, a.ct_name].some(v =>
        (v || '').toLowerCase().includes(q)
      );
    });
  }, [assets, search, dept]);

  const renderItem = ({ item }: any) => {
    const age = assetAge(item.as_purchasedate);
    return (
      <TouchableOpacity
        style={styles.item}
        onPress={() => navigation.navigate('AmAssetDetail', { assetId: item.as_id })}
      >
        <AmPhoto edId={item.photo_id || undefined} size={64} />
        <View style={styles.itemBody}>
          <Text style={styles.title} numberOfLines={1}>{item.as_name}</Text>
          <Text style={styles.sub}>{item.as_code}{item.ct_name ? `  ·  ${item.ct_name}` : ''}{(item.as_qty ?? 1) > 1 ? `  ·  Qty ${item.as_qty}` : ''}</Text>
          {item.dp_name ? <Text style={styles.sub}><T>Dept:</T> {item.dp_name}</Text> : null}
          {item.loc_name || item.as_location ? (
            <Text style={styles.sub} numberOfLines={1}>
              <Icon name="map-marker" size={11} color="#666" /> {item.loc_name || item.as_location}
            </Text>
          ) : null}
          <View style={styles.metaRow}>
            <Text style={[styles.status, item.as_status === 'Active' ? styles.ok : styles.warn]}>{item.as_status}</Text>
            {age ? <Text style={styles.sub}>  <T>·  Age</T> {age}</Text> : null}
          </View>
        </View>
        <Icon name="chevron-right" size={18} color="#999" />
      </TouchableOpacity>
    );
  };

  return (
    <View style={styles.container}>
      <Searchbar placeholder={tp("Search code, name, serial, location")} value={search} onChangeText={setSearch} style={styles.search} />
      {departments.length > 0 && (
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.chips} contentContainerStyle={styles.chipsContent}>
          <Chip selected={!dept} onPress={() => setDept(null)} style={styles.chip}>All</Chip>
          {departments.map(d => (
            <Chip key={d} selected={dept === d} onPress={() => setDept(dept === d ? null : d)} style={styles.chip}>{d}</Chip>
          ))}
        </ScrollView>
      )}
      <View style={styles.countRow}>
        <Text style={styles.count}>{filtered.length} <T>asset(s)</T></Text>
        <TouchableOpacity style={styles.addBtn} onPress={() => navigation.navigate('AmAssetEntry')}>
          <Icon name="plus" size={12} color="#fff" />
          <Text style={styles.addText}> <T>New asset</T></Text>
        </TouchableOpacity>
      </View>
      {isLoading ? (
        <ActivityIndicator size="large" color="#2E7D32" style={{ flex: 1 }} />
      ) : filtered.length === 0 ? (
        <View style={styles.empty}>
          <Icon name="cube" size={50} color="#ccc" />
          <Text style={styles.emptyText}>{search || dept ? 'No assets found' : 'No assets registered yet'}</Text>
        </View>
      ) : (
        <FlatList data={filtered} renderItem={renderItem} keyExtractor={i => String(i.as_id)} contentContainerStyle={{ padding: 10, paddingBottom: 90 }} />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f5f5f5' },
  search: { margin: 10, marginBottom: 6 },
  chips: { flexGrow: 0, flexShrink: 0, height: 44 },
  chipsContent: { paddingHorizontal: 10, gap: 6, alignItems: 'center' },
  chip: { marginRight: 6 },
  countRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginHorizontal: 12, marginTop: 4 },
  count: { fontSize: 12, color: '#666' },
  addBtn: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#2E7D32', borderRadius: 14, paddingHorizontal: 12, paddingVertical: 6 },
  addText: { color: '#fff', fontSize: 12, fontWeight: 'bold' },
  item: { flexDirection: 'row', backgroundColor: '#fff', borderRadius: 8, padding: 10, marginBottom: 8, alignItems: 'center', borderLeftWidth: 4, borderLeftColor: '#2E7D32', minHeight: 84 },
  itemBody: { flex: 1, marginHorizontal: 10 },
  title: { fontSize: 14, fontWeight: 'bold' },
  sub: { fontSize: 12, color: '#666', marginTop: 1 },
  metaRow: { flexDirection: 'row', alignItems: 'center', marginTop: 3 },
  status: { fontSize: 11, fontWeight: 'bold' },
  ok: { color: '#2E7D32' },
  warn: { color: '#EF6C00' },
  empty: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  emptyText: { marginTop: 10, fontSize: 16, color: '#999' }
});

export default AmAssetRegister;
